import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";

import logoImg from "../assets/marca/logo-small-beige.svg";
import underlineRedImg from "../assets/underline-red.svg";
import underlineBeigeImg from "../assets/underline-beige.svg";

function BurgerIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <button className="content-stretch cursor-pointer flex flex-col gap-[3px] items-center justify-center p-[16px] relative rounded-[8px]">
      <motion.div
        aria-hidden="true"
        className="absolute border-[#f2f1de] border-solid inset-[-2px] pointer-events-none rounded-[10px]"
        animate={{ borderWidth: isOpen ? "4px" : "0px" }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      />

      <motion.div
        className="relative rounded-bl-[1px] rounded-br-[1px] rounded-tl-[8px] rounded-tr-[8px] shrink-0 w-[20px]"
        animate={{ height: isOpen ? "4px" : "7px" }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="absolute border-[#f2f1de] border-[1.5px] border-solid inset-[-0.75px] pointer-events-none rounded-bl-[1.75px] rounded-br-[1.75px] rounded-tl-[8.75px] rounded-tr-[8.75px]" />
      </motion.div>

      <motion.div
        className="relative shrink-0 w-[20px]"
        animate={{ height: isOpen ? "7px" : "3px" }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <motion.div
          className="absolute inset-[-25%_-3.75%]"
          animate={{ opacity: isOpen ? 0 : 1 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        >
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 21.5 4.5">
            <g>
              <path d="M0.75 0.75H20.75" stroke="#F2F1DE" strokeLinecap="round" strokeWidth="1.5" />
              <path d="M0.75 3.75H20.75" stroke="#F2F1DE" strokeLinecap="round" strokeWidth="1.5" />
            </g>
          </svg>
        </motion.div>

        <motion.div
          className="absolute inset-[-10.37%_-3.96%_-10.37%_-4.23%]"
          animate={{ opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
        >
          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 21.6 8.5">
            <g>
              <path d="M0.773604 0.726034L20.8454 7.72603" stroke="#F2F1DE" strokeLinecap="round" strokeWidth="1.5" />
              <path d="M0.845381 7.72603L20.8634 0.726035" stroke="#F2F1DE" strokeLinecap="round" strokeWidth="1.5" />
            </g>
          </svg>
        </motion.div>
      </motion.div>

      <div className="h-[3px] relative rounded-[1px] shrink-0 w-[20px]">
        <div className="absolute border-[#f2f1de] border-[1.5px] border-solid inset-[-0.75px] pointer-events-none rounded-[1.75px]" />
      </div>
    </button>
  );
}

interface NavLinkItem {
  id: string;
  label: string;
  type: "section" | "route";
  target: string;
}

interface NavbarProps {
  className?: string;
}

export default function Navbar({ className = "" }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("");
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const isAboutPage = location.pathname === "/about";

  const navLinks = useMemo<NavLinkItem[]>(() => {
    if (isAboutPage) {
      return [
        { id: "home-route", label: "INICIO", type: "route", target: "/" },
        { id: "about-top", label: "NOSOTROS", type: "section", target: "about-top" },
      ];
    }

    return [
      { id: "inicio", label: "INICIO", type: "section", target: "inicio" },
      { id: "comunidad", label: "COMUNIDAD", type: "section", target: "comunidad" },
      { id: "prode", label: "PRODE", type: "section", target: "prode" },
      { id: "burgers", label: "LAS BURGERS", type: "section", target: "burgers" },
      { id: "about", label: "NOSOTROS", type: "section", target: "about" },
      { id: "foodtruck", label: "FOODTRUCK", type: "section", target: "foodtruck" },
      { id: "equipo", label: "EQUIPO", type: "section", target: "equipo" },
    ];
  }, [isAboutPage]);

  useEffect(() => {
    if (isMenuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
  }, [isMenuOpen]);

  useEffect(() => {
    if (isAboutPage) {
      setActiveLink("ABOUT");
      return;
    }

    const updateActiveSection = () => {
      const navbarOffset = 110;
      let closestSection = navLinks[0]?.label ?? "";
      let closestDistance = Number.POSITIVE_INFINITY;

      for (const link of navLinks) {
        if (link.type !== "section") continue;

        const element = document.getElementById(link.target);
        if (!element) continue;

        const rect = element.getBoundingClientRect();
        const distance = Math.abs(rect.top - navbarOffset);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSection = link.label;
        }
      }

      setActiveLink(closestSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
    };
  }, [isAboutPage, navLinks]);

  const handleLinkClick = (link: NavLinkItem) => {
    setActiveLink(link.label);
    setIsMenuOpen(false);

    if (link.type === "route") {
      navigate(link.target);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const element = document.getElementById(link.target);

    if (element) {
      const navbarOffset = 96;
      const elementTop = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementTop - navbarOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleLogoClick = () => {
    if (isAboutPage) {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const hero = document.getElementById("inicio");
    if (hero) {
      const top = hero.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top, behavior: "smooth" });
    }
    
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 ${className}`}
      >
        <div className="glass-navbar overflow-hidden">
          <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-16 py-4">
            <div className="flex items-center justify-between w-full">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className="flex-shrink-0 cursor-pointer"
                onClick={handleLogoClick}
              >
                <img src={logoImg} alt="Logo" className="h-[40px] w-[68px]" />
              </motion.div>

              <div className="hidden lg:flex items-center gap-4 xl:gap-6">
                {navLinks.map((link) => {
                  const isActive = activeLink === link.label;
                  const isHovered = hoveredLink === link.label;
                  const showUnderline = isActive || isHovered;

                  return (
                    <button
                      key={link.id}
                      onClick={() => handleLinkClick(link)}
                      onMouseEnter={() => setHoveredLink(link.label)}
                      onMouseLeave={() => setHoveredLink(null)}
                      className="relative flex flex-col items-center"
                    >
                      <div className="flex h-14 items-center justify-center px-2 xl:px-4 relative">
                        <p
                          className={`font-['Roboto_Mono',monospace] leading-6 text-[#f2f1de] text-sm xl:text-base tracking-[0.32px] whitespace-nowrap transition-all duration-300 hover:text-white ${showUnderline ? "font-bold" : "font-normal"
                            }`}
                        >
                          {link.label}
                        </p>
                      </div>

                      <AnimatePresence>
                        {isActive && (
                          <motion.img
                            src={underlineRedImg}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[41px] w-[122px]"
                            alt=""
                          />
                        )}
                      </AnimatePresence>

                      <AnimatePresence>
                        {isHovered && !isActive && (
                          <motion.img
                            src={underlineBeigeImg}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ type: "spring", stiffness: 400, damping: 25 }}
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[41px] w-[122px]"
                            alt=""
                          />
                        )}
                      </AnimatePresence>
                    </button>
                  );
                })}
              </div>

              <motion.a
                href="https://wa.me/5492613866837?text=%C2%A1Hola%20Stacy!%20Quiero%20hacer%20un%20pedido.%20%C2%BFMe%20comparten%20el%20men%C3%BA%3F"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(186, 70, 67, 0.5)" }}
                whileTap={{ scale: 0.95 }}
               className="hidden lg:flex bg-[#ba4643] items-center justify-center px-4 xl:px-6 py-3 xl:py-4 rounded-lg flex-shrink-0"
              >
                <p className="font-['Roboto_Mono',monospace] font-medium leading-6 xl:leading-8 text-[#f2f1de] text-base xl:text-xl tracking-[0.4px] whitespace-nowrap">
                  PEDÍ AHORA
                </p>
              </motion.a>

              <motion.div
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                whileTap={{ scale: 0.9 }}
                className="lg:hidden flex items-center justify-center flex-shrink-0 cursor-pointer"
              >
                <BurgerIcon isOpen={isMenuOpen} />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "100vh", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="fixed top-0 left-0 right-0 z-[40] lg:hidden overflow-hidden"
            style={{ background: "rgba(0, 0, 0, 0.95)" }}
          >
            <div className="flex flex-col px-6 pt-32 pb-12 space-y-8 min-h-screen">
              {navLinks.map((link, index) => {
                const isActive = activeLink === link.label;

                return (
                  <motion.button
                    key={link.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.1 + index * 0.08,
                      duration: 0.4,
                      ease: "easeOut",
                    }}
                    onClick={() => handleLinkClick(link)}
                    className="relative flex flex-col items-start w-fit"
                  >
                    <div className="flex h-14 items-center justify-center px-4 relative z-10">
                      <p
                        className={`font-['Roboto_Mono',monospace] text-[#f2f1de] text-lg tracking-[0.32px] whitespace-nowrap ${isActive ? "font-bold" : "font-normal"
                          }`}
                      >
                        {link.label}
                      </p>
                    </div>

                    <AnimatePresence>
                      {isActive && (
                        <motion.img
                          src={underlineRedImg}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ type: "spring", stiffness: 400, damping: 25 }}
                          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[41px] w-[122px]"
                          alt=""
                        />
                      )}
                    </AnimatePresence>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}